import { format } from "date-fns";
import { assign, fromPromise, sendTo, setup, waitFor } from "xstate";
import { type ActorRefFrom, type ActorRefFromLogic } from "xstate";

import { remoteDocumentRepoExistsPromise } from "~/state/machines/docsapi/remoteDocumentRepoExists.machine";
import { getGiteaApiUrl } from "~/utils/gitea";

import {
	type AuthenticationMachine,
	authenticationActorRef,
	waitForAccessTokenSilently,
} from "../authentication";
import { coerceToError } from "../coerceToError";
import { createRemoteRepo } from "../docsapi/createRemoteRepo";
import { GIT_PUBLISHING_REMOTE_NAME } from "../git/addGitMomentRemote";
import { type gitRepositoryMachine } from "../git/repository/git-repository.machine";

// const debuglog = (_msg, ...args) => {
// 	console.log(`[transfer machine] ${_msg}`, ...args);
// };
const debuglog = (_msg, ..._) => {};

interface TransferModalContext {
	workspace: string;
	documentId: string;
	dirty: boolean;
	createdRepo: boolean;
	error: Error | null;
	remoteRepoExists: boolean;
	gitRef: ActorRefFrom<typeof gitRepositoryMachine>;
}

interface TransferMachineArgs {
	workspace: string;
	documentId: string;
	authenticationActorRef: ActorRefFromLogic<AuthenticationMachine>;
	gitRef: ActorRefFrom<typeof gitRepositoryMachine>;
}

// In order to avoid doing full consistent reads and writes,
// we do not refresh the data on update success, only on error.
// THus we expect the React component to refresh intelligently when needed.
export const transferMachine = setup({
	types: {
		context: {} as TransferModalContext,
		events: {} as
			| { type: "transfer.attachWorkspace"; workspace: string; documentId: string }
			| { type: "transfer.refresh" },
		input: {} as TransferMachineArgs,
	},
	actors: {
		momentRepoExists: fromPromise(remoteDocumentRepoExistsPromise),
		createRemoteRepo: fromPromise(createRemoteRepo),
		loadContext: fromPromise(
			async ({
				input,
			}: {
				input: {
					documentId: string;
					workspace: string;
				};
			}) => {
				const { documentId, workspace } = input;
				const token = await waitForAccessTokenSilently(authenticationActorRef);
				if (!token) {
					throw new Error("Not logged in");
				}
				const remoteRepoExists = await remoteDocumentRepoExistsPromise({
					input: {
						documentId,
						workspace,
						authenticationActorRef,
					},
				});
				return {
					workspace,
					documentId,
					remoteRepoExists,
					createdRepo: false,
					gitRef: authenticationActorRef,
				};
			}
		),

		attachWorkspace: fromPromise(
			async ({
				input,
			}: {
				input: {
					workspace: string;
				};
			}) => {
				return {
					workspace: input.workspace,
				};
			}
		),
	},
}).createMachine({
	id: "transfer.machine",
	initial: "loading",
	context: ({ input }) => ({
		workspace: input.workspace,
		documentId: input.documentId,
		dirty: false,
		authenticationActorRef: input.authenticationActorRef,
		error: null,
		remoteRepoExists: false,
		createdRepo: false,
		gitRef: input.gitRef,
	}),

	states: {
		loading: {
			entry: [() => debuglog("loading permission state")],
			invoke: {
				src: "loadContext",
				input: ({ context }) => ({
					documentId: context.documentId,
					workspace: context.workspace,
					authenticationActorRef,
				}),
				onDone: {
					actions: [
						({ context }) => {
							debuglog("loaded permission state");
							debuglog(JSON.stringify(context));
						},
					],
					target: "idle",
				},
				onError: {
					target: "error",
					actions: assign({
						error: ({ event }) =>
							"error" in event ? coerceToError(event.error) : null,
					}),
				},
			},
		},
		attachWorkspace: {
			entry: [() => debuglog("attachWorkspace")],
			invoke: {
				src: "attachWorkspace",
				input: ({ event }) => {
					if (event.type !== "transfer.attachWorkspace") {
						throw new Error("Invalid event type");
					}

					return {
						workspace: event.workspace,
						documentId: event.documentId,
						authenticationActorRef,
					};
				},
				onDone: {
					actions: [
						assign({
							workspace: ({ event }) => event.output.workspace,
							dirty: true,
						}),
					],
					target: "createRemoteRepo",
				},
				onError: {
					target: "error",
					actions: assign({
						error: ({ event }) =>
							"error" in event ? coerceToError(event.error) : null,
					}),
				},
			},
		},
		createRemoteRepo: {
			entry: [() => debuglog("createRemoteRepo")],
			invoke: {
				src: "createRemoteRepo",
				input: ({ context }) => ({
					documentId: context.documentId,
					workspaceName: context.workspace,
					authenticationActorRef,
					skip: context.remoteRepoExists,
				}),
				onDone: {
					actions: [
						assign({
							createdRepo: true,
							remoteRepoExists: true,
							dirty: false,
						}),
						({ context }) =>
							debuglog("created remote repo. sending git remote add", context),
						sendTo(
							({ context }) => context.gitRef,
							// @ts-ignore self is an ActorRef but sender does not like it
							({ context, self }) => ({
								type: "git.remote.add",
								payload: {
									remote: GIT_PUBLISHING_REMOTE_NAME,
									url: `${getGiteaApiUrl()}/git/${context.workspace}/${context.documentId}.git`,
								},
								sender: self,
							})
						),
						sendTo(
							({ context }) => context.gitRef,
							// @ts-ignore self is an ActorRef but sender does not like it
							({ self }) => ({
								type: "git.commit",
								payload: {
									message: `Publishing changes ${format(new Date(), "yyyy/MM/dd")}`,
								},
								sender: self,
							})
						),

						async ({ context }) => {
							await waitFor(
								context.gitRef,
								(state) =>
									state.matches({
										commands: "waitingForCommands",
									}),
								{ timeout: 5000 }
							);
						},
					],
					target: "idle",
				},
				onError: {
					target: "error",
					actions: assign({
						error: ({ event }) =>
							"error" in event ? coerceToError(event.error) : null,
					}),
				},
			},
		},

		idle: {
			entry: [() => debuglog("in idle state")],
			on: {
				"transfer.refresh": {
					target: "loading",
					actions: [
						assign({
							dirty: true,
						}),
					],
				},
				"transfer.attachWorkspace": {
					target: "attachWorkspace",
					actions: assign({
						dirty: true,
					}),
				},
			},
		},

		error: {
			on: {
				"transfer.refresh": {
					target: "loading",
				},
			},
			entry: [
				(event, context) => debuglog("error loading permission state", { event, context }),
			],
		},
	},
});
